import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  Upload,
  X,
  Save,
  FolderOpen,
  AlertCircle,
  Check,
  FileImage,
} from "lucide-react";

const ResidentialAdminPanel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [editProjectName, setEditProjectName] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Fetch residential projects
  const fetchProjects = async () => {
    try {
      const response = await fetch(
        "https://archpoint.onrender.com/api/residential",
      );
      const data = await response.json();
      setProjects(data);
      setLoading(false);
    } catch (error) {
      showNotification("Error fetching projects", "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      3000,
    );
  };

  const showConfirm = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (confirmAction) confirmAction();
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage("");
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage("");
  };

  // Create new project
  const handleCreateProject = async () => {
    if (!newProjectName.trim())
      return showNotification("Enter a project name", "error");

    try {
      const response = await fetch(
        "https://archpoint.onrender.com/api/residential",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newProjectName }),
        },
      );

      if (response.ok) {
        showNotification("Project created successfully!", "success");
        setNewProjectName("");
        setShowAddModal(false);
        fetchProjects();
      } else showNotification("Failed to create project", "error");
    } catch (error) {
      showNotification("Error creating project", "error");
    }
  };

  // Update project name
  const handleUpdateProject = async () => {
    if (!editProjectName.trim() || !selectedProject) return;

    try {
      const response = await fetch(
        `https://archpoint.onrender.com/api/residential/${selectedProject._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: editProjectName }),
        },
      );

      if (response.ok) {
        showNotification("Project updated successfully!", "success");
        setShowEditModal(false);
        setSelectedProject(null);
        fetchProjects();
      } else showNotification("Failed to update project", "error");
    } catch (error) {
      showNotification("Error updating project", "error");
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    showConfirm(
      "Are you sure you want to delete this project? All images will be removed.",
      async () => {
        try {
          const response = await fetch(
            `https://archpoint.onrender.com/api/residential/${projectId}`,
            { method: "DELETE" },
          );
          if (response.ok) {
            showNotification("Project deleted successfully!", "success");
            fetchProjects();
          } else showNotification("Failed to delete project", "error");
        } catch (error) {
          showNotification("Error deleting project", "error");
        }
      },
    );
  };

  // Upload images
  const handleUploadImages = async () => {
    if (uploadFiles.length === 0 || !selectedProject)
      return showNotification("Select files to upload", "error");

    const formData = new FormData();
    uploadFiles.forEach((file) => formData.append("images", file));

    try {
      const response = await fetch(
        `https://archpoint.onrender.com/api/residential/${selectedProject._id}/images`,
        { method: "POST", body: formData },
      );

      if (response.ok) {
        showNotification("Images uploaded successfully!", "success");
        setUploadFiles([]);
        setShowUploadModal(false);
        setSelectedProject(null);
        fetchProjects();
      } else showNotification("Failed to upload images", "error");
    } catch (error) {
      showNotification("Error uploading images", "error");
    }
  };

  // Delete single image
  const handleDeleteImage = async (projectId, imageUrl) => {
    showConfirm("Are you sure you want to delete this image?", async () => {
      try {
        const response = await fetch(
          `https://archpoint.onrender.com/api/residential/${projectId}/images`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageUrl }),
          },
        );

        if (response.ok) {
          showNotification("Image deleted successfully!", "success");
          fetchProjects();
        } else showNotification("Failed to delete image", "error");
      } catch (error) {
        showNotification("Error deleting image", "error");
      }
    });
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setEditProjectName(project.name);
    setShowEditModal(true);
  };

  const openUploadModal = (project) => {
    setSelectedProject(project);
    setShowUploadModal(true);
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-2 ${
            notification.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {notification.type === "success" ? (
            <Check size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">
            Residential <span className="text-yellow-500">Admin Panel</span>
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Add New Project
          </button>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project._id}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              {/* Project Header */}
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white">
                    {project.name}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(project)}
                      className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                    >
                      <Edit2 size={18} className="text-white" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project._id)}
                      className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                    >
                      <Trash2 size={18} className="text-white" />
                    </button>
                  </div>
                </div>
                <p className="text-yellow-100 text-sm mt-1">
                  {project.images?.length || 0} images
                </p>
              </div>

              {/* Project Images */}
              <div className="p-4">
                {project.images && project.images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {project.images.slice(0, 8).map((image, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={image}
                          alt={`${project.name} ${idx + 1}`}
                          className="w-full h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleDeleteImage(project._id, image)}
                          className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        >
                          <Trash2 size={16} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                    <FolderOpen size={48} className="mb-2" />
                    <p className="text-sm">No images yet</p>
                  </div>
                )}

                <button
                  onClick={() => openUploadModal(project)}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Upload size={18} className="mr-2" />
                  Upload Images
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {projects.length === 0 && (
          <div className="text-center py-20">
            <FolderOpen size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-2xl font-semibold text-gray-600 mb-2">
              No Projects Yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first project to get started
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Plus size={20} className="inline mr-2" />
              Add New Project
            </button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Add New Project
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Project Name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center"
              >
                <Save size={18} className="mr-2" />
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Edit Project</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <input
              type="text"
              value={editProjectName}
              onChange={(e) => setEditProjectName(e.target.value)}
              placeholder="Project Name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateProject}
                className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center"
              >
                <Save size={18} className="mr-2" />
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Images Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                Upload Images
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4">
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FileImage size={40} className="text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    Click to select images
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, JPEG</p>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                  className="hidden"
                />
              </label>
            </div>

            {uploadFiles.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  {uploadFiles.length} file(s) selected
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} className="text-xs text-gray-500 truncate">
                      {file.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadImages}
                className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center"
              >
                <Upload size={18} className="mr-2" />
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start mb-6">
              <div className="flex-shrink-0">
                <AlertCircle size={24} className="text-yellow-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Confirm Action
                </h3>
                <p className="text-gray-600">{confirmMessage}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCancelConfirm}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidentialAdminPanel;
